import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/sessoes')({ component: AdminSessoes });

const money = (v: any) => Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

type Status = 'PENDING' | 'CONFIRMED' | 'DONE' | 'DISPUTED' | 'CANCELLED';

const STATUSES: { key: Status | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'Todas' },
  { key: 'PENDING', label: 'Aguardando' },
  { key: 'CONFIRMED', label: 'Confirmadas' },
  { key: 'DONE', label: 'Concluídas' },
  { key: 'DISPUTED', label: 'Em disputa' },
  { key: 'CANCELLED', label: 'Canceladas' },
];

const STATUS_LABELS: Record<Status, string> = {
  PENDING: 'Aguardando', CONFIRMED: 'Confirmada', DONE: 'Concluída',
  DISPUTED: 'Em disputa', CANCELLED: 'Cancelada',
};

const STATUS_STYLES: Record<Status, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  CONFIRMED: 'bg-teal-50 text-teal-700 border-teal-200',
  DONE: 'bg-slate-50 text-slate-600 border-slate-200',
  DISPUTED: 'bg-orange-50 text-orange-700 border-orange-200',
  CANCELLED: 'bg-red-50 text-red-600 border-red-200',
};

function AdminSessoes() {
  const [filter, setFilter] = useState<Status | 'ALL'>('ALL');
  const [therapistId, setTherapistId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const { data: therapists = [] } = useQuery({
    queryKey: ['admin-therapists'],
    queryFn: () => api.get('admin/therapists').json<any[]>(),
  });

  const args = { status: filter === 'ALL' ? '' : filter, therapistId, from, to, page };
  const { data, isLoading } = useQuery({
    queryKey: ['admin-sessions', args],
    queryFn: () => {
      const sp = new URLSearchParams();
      Object.entries(args).forEach(([k, v]) => v && sp.set(k, String(v)));
      return api.get(`admin/sessions?${sp.toString()}`).json<{ items: any[]; total: number; pages: number }>();
    },
  });

  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  return (
    <>
      <AdminPageHeader title="Sessões" subtitle={`${data?.total ?? '...'} no total · página ${page}/${totalPages}`} />

      {/* Tabs de status */}
      <div className="flex flex-wrap gap-2 mb-3">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => { setFilter(s.key); setPage(1); }}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium transition border',
              filter === s.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Filtros adicionais */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 grid md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Profissional</span>
          <select
            value={therapistId}
            onChange={(e) => { setTherapistId(e.target.value); setPage(1); }}
            className="mt-1 w-full bg-white border border-slate-200 rounded-md h-9 px-3 text-sm"
          >
            <option value="">Todos</option>
            {therapists.map((t: any) => <option key={t.id} value={t.id}>{t.user?.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">De</span>
          <input
            type="date"
            value={from}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }}
            className="mt-1 w-full bg-white border border-slate-200 rounded-md h-9 px-3 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Até</span>
          <input
            type="date"
            value={to}
            onChange={(e) => { setTo(e.target.value); setPage(1); }}
            className="mt-1 w-full bg-white border border-slate-200 rounded-md h-9 px-3 text-sm"
          />
        </label>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Profissional ↔ Cliente</th>
                <th className="text-left px-5 py-3 font-semibold">Data · Duração</th>
                <th className="text-left px-5 py-3 font-semibold">Modalidade</th>
                <th className="text-right px-5 py-3 font-semibold">Valor</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan={5} className="p-12 text-center text-sm text-slate-400">Carregando...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="p-12 text-center text-sm text-slate-400">Nenhuma sessão.</td></tr>
              ) : items.map((s) => {
                const dimmed = s.status === 'CANCELLED' || s.status === 'DONE';
                return (
                  <tr key={s.id} className={cn('hover:bg-slate-50/60', dimmed && 'opacity-60')}>
                    <td className="px-5 py-3 min-w-0">
                      <div className="font-semibold text-slate-800 leading-tight truncate">{s.therapist?.user?.name ?? '—'}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5 truncate">↔ {s.client?.user?.name ?? '—'}</div>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600 whitespace-nowrap">
                      <div>
                        {new Date(s.scheduledAt).toLocaleString('pt-BR', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
                        })}
                      </div>
                      <div className="text-slate-400 mt-0.5">{s.durationMin} min</div>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600">
                      {s.modality === 'ONLINE' ? 'Online' : s.modality === 'PRESENCIAL' || s.modality === 'IN_PERSON' ? 'Presencial' : 'Híbrido'}
                    </td>
                    <td className="px-5 py-3 text-right text-xs tabular-nums text-slate-700">
                      {s.priceCharged ? money(s.priceCharged) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      <span className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap', STATUS_STYLES[s.status as Status] ?? 'bg-slate-100 text-slate-600')}>
                        {STATUS_LABELS[s.status as Status] ?? s.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-slate-100 text-xs">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="text-teal-600 hover:underline disabled:text-slate-300 disabled:no-underline"
            >← Anterior</button>
            <span className="text-slate-500">Página {page} de {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="text-teal-600 hover:underline disabled:text-slate-300 disabled:no-underline"
            >Próxima →</button>
          </div>
        )}
      </div>
    </>
  );
}
